import { z } from "zod";

import { AiEvaluationBudgetSchema } from "../../../schemas/workflow-presets/ai-evaluation-budget.schema";
import { SatisfactionEvaluationPresetSchema } from "../../../schemas/workflow-presets/satisfaction-evaluation-preset.schema";
import { UuidRegex } from "../../../shared/regexes/uuid.regex";
import { ApiSuccessSchema } from "../../common/response.schema";

const UuidString = z.string().refine((v) => UuidRegex.test(v), { message: "Must be a UUID" });

/**
 * Materialise the satisfaction-evaluation preset on a deployment.
 *
 * Body is empty — the use case re-resolves the active config from the
 * deployment override + org default rather than letting the client mint
 * config inline. Keeps the editor settings UI as the single source of
 * truth and prevents API consumers from bypassing the inheritance rule.
 */
const MaterialiseSatisfactionResponseSchema = z.object({
  workflowId: z.string(),
  presetSource: z.literal("SATISFACTION_EVALUATION"),
  configSource: z.enum(["deployment", "organization"])
});

const UpdateSatisfactionConfigBodySchema = z.object({
  /**
   * Per-deployment config override. Pass `null` to clear the override
   * and inherit Organization.satisfactionPresetDefaultConfig instead.
   */
  config: SatisfactionEvaluationPresetSchema.nullable()
});

const UpdateSatisfactionConfigResponseSchema = z.object({
  deploymentId: z.string(),
  config: SatisfactionEvaluationPresetSchema.nullable()
});

/**
 * Per-deployment config snapshot for the settings UI. Returns the active
 * config plus its source (deployment override, org default, or none) so
 * the UI can render an "Inherited from organization default" indicator
 * without a second round-trip.
 *
 * `override` is the resolved deployment-scoped config when present;
 * `hasExplicitOverride` distinguishes "this deployment has no opinion
 * yet" (null) from "this deployment explicitly opted in to inheritance"
 * (false override + true hasExplicitOverride is impossible by
 * construction — `hasExplicitOverride` only flips true when a non-null
 * config is stored on the deployment).
 */
const GetDeploymentConfigResponseSchema = z.object({
  deploymentId: z.string(),
  override: SatisfactionEvaluationPresetSchema.nullable(),
  resolved: SatisfactionEvaluationPresetSchema.nullable(),
  source: z.enum(["deployment", "organization", "none"]),
  hasExplicitOverride: z.boolean()
});

/**
 * Org-wide default config for the satisfaction preset. Read returns the
 * raw Json value parsed through the Zod schema (or null if unset). Write
 * accepts null to clear the default — deployments that were inheriting
 * fall back to "no satisfaction evaluation" until either an override or
 * a new org default is set.
 */
const GetOrgDefaultResponseSchema = z.object({
  config: SatisfactionEvaluationPresetSchema.nullable()
});

const UpdateOrgDefaultBodySchema = z.object({
  config: SatisfactionEvaluationPresetSchema.nullable()
});

const UpdateOrgDefaultResponseSchema = z.object({
  config: SatisfactionEvaluationPresetSchema.nullable()
});

/**
 * Org-visible read of the AI-evaluation budget. Returns caps + running
 * counters so the org settings panel can render "Daily cap: $X, Spent
 * today: $Y" without an admin round-trip. Edits are admin-only via the
 * console-ui surface — this is intentionally read-only at the org level.
 *
 * `null` when no budget has ever been configured for the org.
 */
const GetOrgBudgetResponseSchema = z.object({
  budget: AiEvaluationBudgetSchema.nullable()
});

/**
 * Detach a managed preset workflow back into a hand-edited workflow.
 * Flips `isManaged=false`, clears `presetSource` + `presetConfig` on
 * the same row, returns the updated Workflow id so the editor can
 * refetch the editable state.
 */
const DetachPresetResponseSchema = z.object({
  workflowId: z.string(),
  isManaged: z.literal(false)
});

export const ZWorkflowPresets = {
  MaterialiseSatisfaction: {
    method: "POST" as const,
    path: "/api/deployments/:deploymentId/presets/satisfaction",
    PathVars: z.object({ deploymentId: UuidString }),
    Body: z.object({}).optional(),
    Response: ApiSuccessSchema(MaterialiseSatisfactionResponseSchema)
  },

  GetDeploymentConfig: {
    method: "GET" as const,
    path: "/api/deployments/:deploymentId/presets/satisfaction/config",
    PathVars: z.object({ deploymentId: UuidString }),
    Response: ApiSuccessSchema(GetDeploymentConfigResponseSchema)
  },

  UpdateSatisfactionConfig: {
    method: "PUT" as const,
    path: "/api/deployments/:deploymentId/presets/satisfaction/config",
    PathVars: z.object({ deploymentId: UuidString }),
    Body: UpdateSatisfactionConfigBodySchema,
    Response: ApiSuccessSchema(UpdateSatisfactionConfigResponseSchema)
  },

  GetOrgDefault: {
    method: "GET" as const,
    path: "/api/organizations/me/presets/satisfaction-default",
    Response: ApiSuccessSchema(GetOrgDefaultResponseSchema)
  },

  UpdateOrgDefault: {
    method: "PUT" as const,
    path: "/api/organizations/me/presets/satisfaction-default",
    Body: UpdateOrgDefaultBodySchema,
    Response: ApiSuccessSchema(UpdateOrgDefaultResponseSchema)
  },

  GetOrgBudget: {
    method: "GET" as const,
    path: "/api/organizations/me/presets/ai-evaluation-budget",
    Response: ApiSuccessSchema(GetOrgBudgetResponseSchema)
  },

  DetachWorkflow: {
    method: "POST" as const,
    path: "/api/workflows/:workflowId/detach-preset",
    PathVars: z.object({ workflowId: UuidString }),
    Body: z.object({}).optional(),
    Response: ApiSuccessSchema(DetachPresetResponseSchema)
  }
} as const;
